import { PositionSide } from '@kwenta/sdk/types'
import { formatDollars } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { type ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import AcceptWarningView from 'components/AcceptWarningView'
import BaseModal from 'components/BaseModal'
import Button from 'components/Button'
import ErrorView from 'components/ErrorView'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { setShowEditPositionModal } from 'state/app/reducer'
import { selectAckedOrdersWarning } from 'state/app/selectors'
import { clearTradeInputs, handleOneClickOperation } from 'state/futures/actions'
import { selectPerpsProvider, selectSlTpModalInputs } from 'state/futures/common/selectors'
import {
	changeSLTPAmount,
	changeSLTPPrice,
	setPartialStopLossAndTakeProfitIsolatedMargin,
	updateStopLossAndTakeProfitIsolatedMargin,
} from 'state/futures/isolatedMargin/actions'
import {
	selectDelegationTradingWarning,
	selectIsIsolatedMarginProvider,
	selectModalSLTPValidity,
	selectOneClickTradeBtnI18n,
	selectSLTPModalInfo,
	selectSubmittingFuturesTx,
} from 'state/futures/selectors'
import {
	grantMarginEnginePermission,
	submitCrossMarginPartialSLTP,
	submitCrossMarginSLTPUpdates,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectCondOrdersEnabled,
	selectMarginEnginePermitted,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import PositionType from '../PositionType'
import useAbstractionInfo from '../Trade/OneClickTrading/hooks/useAbstractionInfo'
import OrderAcknowledgement from '../Trade/OrderAcknowledgement'

import UnderlineTabs from 'components/Button/UnderlineTabs'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import { clearSLTPModalInputs } from 'state/futures/reducer'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import { PositionSizeInput } from '../ClosePositionModal/ClosePositionSizeInput'
import EditStopLossAndTakeProfitInput from './EditStopLossAndTakeProfitInput'
import StopLossAndTakeProfitSummary from './StopLossAndTakeProfitSummary'

type EditType = 'entire' | 'partial'

const EditStopLossAndTakeProfitModal = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)

	const { takeProfitPrice, stopLossPrice, takeProfitAmount, stopLossAmount } = useAppSelector(
		selectSlTpModalInputs,
		isEqual
	)

	const hideOrderWarning = useAppSelector(selectAckedOrdersWarning)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const showDelegationTradingWarning = useAppSelector(selectDelegationTradingWarning)
	const oneClickBtnI18n = useAppSelector(selectOneClickTradeBtnI18n)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)

	const condOrdersEnabled = useAppSelector(selectCondOrdersEnabled)
	const isIsolatedMargin = useAppSelector(selectIsIsolatedMarginProvider)

	const [editType, setEditType] = useState<EditType>('entire')
	const onChangeTab = useCallback(
		(selection: EditType) => {
			dispatch(clearSLTPModalInputs())
			if (selection === 'entire') {
				dispatch(changeSLTPPrice(existSLPrice, 'stop-loss'))
				dispatch(changeSLTPPrice(existTPPrice, 'take-profit'))
			}
			setEditType(selection)
		},
		[dispatch]
	)

	const editTabs = useMemo(
		() => [
			{
				name: 'entire',
				label: t('futures.market.trade.edit-sl-tp.entire-position'),
				active: editType === 'entire',
				onClick: (): void => {
					onChangeTab('entire')
				},
			},
			{
				name: 'partial',
				label: t('futures.market.trade.edit-sl-tp.partial-position'),
				active: editType === 'partial',
				onClick: (): void => {
					onChangeTab('partial')
				},
			},
		],
		[editType, onChangeTab, t]
	)

	const sltpValidity = useAppSelector((state) => {
		const validity = selectModalSLTPValidity(state)
		return {
			stopLoss: {
				showWarning: validity.stopLoss.showWarning,
				invalidLabel: validity.stopLoss.invalidLabel,
			},
			takeProfit: {
				invalidLabel: validity.takeProfit.invalidLabel,
			},
		}
	}, isEqual)

	const {
		market,
		position,
		takeProfitPrice: existTPPrice,
		stopLossPrice: existSLPrice,
		order,
		marketPrice,
	} = useAppSelector(selectSLTPModalInfo)

	const [acceptedSLRisk, setAcceptedSLRisk] = useState(false)

	const { isSessionActive } = useAbstractionInfo()

	const [showOrderWarning, setShowOrderWarning] = useState(
		!existSLPrice && !existTPPrice && !hideOrderWarning
	)

	const hasInputValues = useMemo(
		() => !isEmpty(takeProfitPrice) || !isEmpty(stopLossPrice),
		[takeProfitPrice, stopLossPrice]
	)

	const hasOrders = useMemo(() => !!order, [order])

	const hasChangeOrders = useMemo(() => {
		return hasOrders && (existTPPrice !== takeProfitPrice || existSLPrice !== stopLossPrice)
	}, [hasOrders, stopLossPrice, existSLPrice, existTPPrice, takeProfitPrice])

	const isActive = useMemo(
		() =>
			!showDelegationTradingWarning &&
			!(sltpValidity.stopLoss.showWarning && !acceptedSLRisk) &&
			!sltpValidity.stopLoss.invalidLabel &&
			!sltpValidity.takeProfit.invalidLabel &&
			(editType === 'entire' ||
				(editType === 'partial' &&
					(!isEmpty(takeProfitPrice) || !isEmpty(stopLossPrice)) &&
					takeProfitAmount !== '0' &&
					stopLossAmount !== '0')) &&
			(hasOrders
				? hasInputValues
					? hasChangeOrders
					: takeProfitPrice !== undefined || stopLossPrice !== undefined
				: hasInputValues),
		[
			showDelegationTradingWarning,
			acceptedSLRisk,
			hasChangeOrders,
			hasInputValues,
			editType,
			takeProfitAmount,
			stopLossAmount,
			hasOrders,
			stopLossPrice,
			takeProfitPrice,
			sltpValidity.stopLoss.showWarning,
			sltpValidity.stopLoss.invalidLabel,
			sltpValidity.takeProfit.invalidLabel,
		]
	)

	const entryPriceWei = useMemo(() => {
		return wei(position?.details.price.avgEntryPrice ?? 0)
	}, [position?.details.price.avgEntryPrice])

	const calculateSizeWei = useMemo(() => {
		if (!position?.details.size) {
			return wei(0)
		}

		return wei(position.details.size).mul(entryPriceWei)
	}, [position?.details.size, entryPriceWei])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		dispatch(clearTradeInputs())
		dispatch(clearSLTPModalInputs())
		dispatch(changeSLTPPrice(existSLPrice, 'stop-loss'))
		dispatch(changeSLTPPrice(existTPPrice, 'take-profit'))
	}, [])

	const onChangeStopLossPrice = useCallback(
		(_: ChangeEvent<HTMLInputElement>, v: string) => {
			dispatch(changeSLTPPrice(v, 'stop-loss'))
		},
		[dispatch]
	)

	const onChangeStopLossAmount = useCallback(
		(amount: string) => {
			dispatch(changeSLTPAmount(amount, 'stop-loss'))
		},
		[dispatch]
	)

	const onChangeTakeProfitPrice = useCallback(
		(_: ChangeEvent<HTMLInputElement>, v: string) => {
			dispatch(changeSLTPPrice(v, 'take-profit'))
		},
		[dispatch]
	)

	const onChangeTakeProfitAmount = useCallback(
		(amount: string) => {
			dispatch(changeSLTPAmount(amount, 'take-profit'))
		},
		[dispatch]
	)

	const onSetStopLossAndTakeProfit = useCallback(() => {
		if (editType === 'entire') {
			if (isIsolatedMargin) {
				dispatch(
					updateStopLossAndTakeProfitIsolatedMargin({
						updateStopLoss: true,
						updateTakeProfit: true,
					})
				)
			} else {
				dispatch(submitCrossMarginSLTPUpdates())
			}
		} else {
			// new limit order
			if (isIsolatedMargin) {
				dispatch(setPartialStopLossAndTakeProfitIsolatedMargin())
			} else {
				dispatch(submitCrossMarginPartialSLTP())
			}
		}
	}, [editType, isIsolatedMargin, dispatch])

	const onClick = useCallback(() => {
		if (condOrdersEnabled && providerIsCrossMargin(perpsProvider) && !marginEnginePermitted) {
			dispatch(grantMarginEnginePermission())
			return
		}

		dispatch(handleOneClickOperation(onSetStopLossAndTakeProfit))
	}, [
		dispatch,
		onSetStopLossAndTakeProfit,
		perpsProvider,
		marginEnginePermitted,
		condOrdersEnabled,
	])

	const onClose = useCallback(() => {
		dispatch(setShowEditPositionModal(null))
	}, [dispatch])

	const buttonText = useMemo(() => {
		if (providerIsCrossMargin(perpsProvider) && condOrdersEnabled && !marginEnginePermitted) {
			return t('futures.market.trade.confirmation.modal.approve-margin-engine')
		}
		if (oneClickBtnI18n && !isSessionActive && !abstractionWalletEnabled) return t(oneClickBtnI18n)
		return t('futures.market.trade.edit-sl-tp.confirm')
	}, [
		t,
		oneClickBtnI18n,
		isSessionActive,
		abstractionWalletEnabled,
		perpsProvider,
		marginEnginePermitted,
		condOrdersEnabled,
	])

	return (
		<StyledBaseModal
			title={
				<FlexDivCol>
					<Body>{t('futures.market.trade.edit-sl-tp.title')}</Body>
					<FlexDivRowCentered justifyContent="left">
						<Body weight="bold" color="secondary">
							{market?.marketName}
						</Body>
						<Spacer width={5} />
						<PositionType side={position?.details.side ?? PositionSide.LONG} />
					</FlexDivRowCentered>
				</FlexDivCol>
			}
			rndProps={{ disableDragging: true, enableResizing: false }}
			isOpen
			onDismiss={onClose}
		>
			<StyledSpacer marginTop={6} />
			<Spacer height={10} />

			<FlexDivRow>
				<FlexDivCol rowGap="4px">
					<Body color="secondary">{t('futures.market.trade.edit-sl-tp.entry-price')}</Body>
					<Body>{formatDollars(entryPriceWei, { suggestDecimals: true })}</Body>
				</FlexDivCol>
				<FlexDivCol rowGap="4px" style={{ minWidth: '100px' }}>
					<Body color="secondary">{t('futures.market.trade.edit-sl-tp.last-price')}</Body>
					<Body>{formatDollars(marketPrice || 0, { suggestDecimals: true })}</Body>
				</FlexDivCol>
				<FlexDivCol rowGap="4px">
					<Body color="secondary">{t('futures.market.trade.edit-sl-tp.liquidation-price')}</Body>
					<Body>
						{formatDollars(position?.details.price.liquidationPrice || 0, {
							suggestDecimals: true,
						})}
					</Body>
				</FlexDivCol>
			</FlexDivRow>
			<Spacer height={10} />

			<TabButtonsContainer>
				<UnderlineTabs options={editTabs} />
			</TabButtonsContainer>
			<Spacer height={20} />
			{showOrderWarning ? (
				<OrderAcknowledgement inContainer onClick={() => setShowOrderWarning(false)} />
			) : (
				<>
					<EditStopLossAndTakeProfitInput
						dataTestId="sltp-modal-take-profit-price-input"
						type={'take-profit'}
						invalidLabel={sltpValidity.takeProfit.invalidLabel}
						price={entryPriceWei}
						value={takeProfitPrice}
						positionSide={position?.details.side ?? PositionSide.LONG}
						leverage={wei(position?.details.margin.leverage || 1)}
						size={calculateSizeWei}
						onChange={onChangeTakeProfitPrice}
					/>
					{editType === 'partial' && (
						<PositionSizeInput
							dataTestId="sltp-modal-take-profit-size-input"
							maxNativeValue={position?.details.size || '0'}
							onSizeChange={onChangeTakeProfitAmount}
							title={t('futures.market.trade.edit-sl-tp.quantity')}
							value={takeProfitAmount}
						/>
					)}
					<StopLossAndTakeProfitSummary
						type={'take-profit'}
						entryPrice={entryPriceWei.toString()}
						value={takeProfitPrice}
						sizePercentage={
							isEmpty(takeProfitAmount) || isEmpty(position?.details.size)
								? '1'
								: wei(takeProfitAmount).div(wei(position?.details.size)).toString()
						}
						positionSide={position?.details.side ?? PositionSide.LONG}
						leverage={position?.details.margin.leverage || '1'}
						enterNotionalSize={calculateSizeWei.toString()}
					/>
					<Spacer height={10} />

					<EditStopLossAndTakeProfitInput
						type={'stop-loss'}
						dataTestId="sltp-modal-stop-loss-price-input"
						positionSide={position?.details.side ?? PositionSide.LONG}
						leverage={wei(position?.details.margin.leverage || 1)}
						invalidLabel={sltpValidity.stopLoss.invalidLabel}
						price={entryPriceWei}
						value={stopLossPrice}
						size={calculateSizeWei}
						onChange={onChangeStopLossPrice}
					/>
					{editType === 'partial' && (
						<PositionSizeInput
							dataTestId="sltp-modal-stop-loss-size-input"
							maxNativeValue={position?.details.size || '0'}
							onSizeChange={onChangeStopLossAmount}
							title={t('futures.market.trade.edit-sl-tp.quantity')}
							value={stopLossAmount}
						/>
					)}
					<StopLossAndTakeProfitSummary
						type={'stop-loss'}
						positionSide={position?.details.side ?? PositionSide.LONG}
						leverage={position?.details.margin.leverage || '1'}
						entryPrice={entryPriceWei.toString()}
						value={stopLossPrice}
						sizePercentage={
							isEmpty(stopLossAmount) || isEmpty(position?.details.size)
								? '1'
								: wei(stopLossAmount).div(wei(position?.details.size)).toString()
						}
						enterNotionalSize={calculateSizeWei.toString()}
					/>

					<Spacer height={4} />
					{sltpValidity.stopLoss.showWarning && (
						<AcceptWarningView
							id="sl-risk-warning"
							style={{ margin: '0 0 20px 0' }}
							message={t('futures.market.trade.confirmation.modal.stop-loss-warning')}
							checked={acceptedSLRisk}
							onChangeChecked={(checked) => setAcceptedSLRisk(checked)}
						/>
					)}

					<Button
						loading={isSubmitting}
						variant="solid-highlight"
						data-testid="sl-tp-modal-submit-button"
						disabled={!isActive}
						fullWidth
						onClick={onClick}
					>
						{buttonText}
					</Button>
					{showDelegationTradingWarning && (
						<ErrorView
							messageType="error"
							message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
							containerStyle={{ margin: '16px 0 0 0' }}
						/>
					)}
				</>
			)}
		</StyledBaseModal>
	)
})

export default EditStopLossAndTakeProfitModal

const StyledSpacer = styled(Spacer)<{ marginTop?: number }>`
	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	width: 100%;
	margin: ${(props) => props.marginTop ?? 20}px 0px 15px;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 438px;
	}
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`
